/**
 * 项目详情页引导步骤（动态版）
 * 模式与 bzmj 一致：先高亮菜单Tab → 再跳转到页面逐个高亮模块介绍
 *
 * Tab 选择器使用 href 属性匹配，避免 nth-child 因权限动态变化导致错位
 * 例: a.pl-sub-tab[href*="/overview"]
 */
export default function createProjectDetailSteps(projectId) {
    var base = '/project/' + projectId
    // Tab 选择器辅助
    function tab(path) {
        return 'a.pl-sub-tab[href*="/' + path + '"]'
    }
    return [
        // ========= 概览页 =========
        {
            element: tab('overview'),
            route: base + '/overview',
            popover: {
                title: '概览',
                description: '点击进入项目概览页，查看项目总体情况。',
                side: 'bottom'
            }
        },
        {
            element: '.project-overview > .row:nth-child(1) > .card:nth-child(1)',
            route: base + '/overview',
            popover: {
                title: '项目信息',
                description: '展示项目基本信息：\n• 项目名称和代号\n• 项目起止时间\n• 当前项目状态（进行中/已完成）',
                side: 'right'
            }
        },
        {
            element: '.project-overview > .row:nth-child(1) > .card:nth-child(2)',
            route: base + '/overview',
            popover: {
                title: '项目进度',
                description: '实时追踪项目完成进度：\n• 完成率百分比和进度条\n• 开始/截止时间和剩余天数\n• 风险预警（绿色=正常、黄色=滞后、红色=严重滞后）',
                side: 'left'
            }
        },
        {
            element: '.project-overview > .row:nth-child(2) > .card:nth-child(1)',
            route: base + '/overview',
            popover: {
                title: '项目成员',
                description: '查看项目参与人员：\n• 按角色分类统计人数（研究/QC/执行/代理等）\n• 头像列表展示成员',
                side: 'right'
            }
        },
        {
            element: '.project-overview > .row:nth-child(2) > .card:nth-child(2)',
            route: base + '/overview',
            popover: {
                title: '波次回收进度',
                description: '各波次的检查任务完成情况：\n• 每个波次按环节展示进度条\n• 显示已通过/总量的比例\n• 进度条颜色：蓝色=进行中，绿色=已完成',
                side: 'left'
            }
        },
        {
            element: '.audit-summary-row',
            route: base + '/overview',
            popover: {
                title: '审核概览',
                description: '快速掌握审核状况：\n• 已审定总量：已完成审核的答卷数\n• 待审核任务：等待审核的数量\n• 审核异常：被驳回或有问题的数量',
                side: 'top'
            }
        },
        {
            element: '.card.full-width',
            route: base + '/overview',
            popover: {
                title: '数据审核进度',
                description: '按波次和环节展示详细审核进度：\n• 已提交：答卷提交数量和比例\n• 一审/二审/三审：多级审核的通过情况\n• 点击"查看详细报告"可进入波次报告页面',
                side: 'top'
            }
        },

        // ========= 我的问卷 =========
        {
            element: tab('forms'),
            route: base + '/forms',
            popover: {
                title: '我的问卷',
                description: '管理项目中的检查问卷。',
                side: 'bottom'
            }
        },
        {
            element: '.project-forms .toolbar',
            route: base + '/forms',
            popover: {
                title: '问卷操作栏',
                description: '• 点击「新建问卷」创建检查问卷\n• 右侧可搜索、筛选状态和类型\n• 切换卡片/表格视图',
                side: 'bottom'
            }
        },
        {
            element: '.project-forms .content',
            route: base + '/forms',
            popover: {
                title: '问卷列表',
                description: '展示项目中的所有问卷：\n• 卡片显示问卷名称和状态\n• 点击问卷卡片可编辑、预览、发布\n\n创建问卷后，才能在波次中分配检查任务。',
                side: 'top',
                align: 'start'
            }
        },

        // ========= 波次管理 =========
        {
            element: tab('waves'),
            route: base + '/waves',
            popover: {
                title: '波次管理',
                description: '配置检查批次和任务分配。',
                side: 'bottom'
            }
        },
        {
            element: '.waves-header',
            route: base + '/waves',
            popover: {
                title: '波次操作栏',
                description: '• 点击「新建波次」创建检查批次\n• 设置波次名称、关联问卷、时间范围\n• 支持多阶段检查配置',
                side: 'bottom'
            }
        },
        {
            element: '.table-card',
            route: base + '/waves',
            popover: {
                title: '波次列表',
                description: '展示所有波次信息：\n• 波次名称和关联问卷\n• 状态（未开始/进行中/已结束）\n• 经销商数量\n• 操作：上传经销商名单、生成码、导出、编辑等',
                side: 'top',
                align: 'start'
            }
        },

        // ========= 数据管理 =========
        {
            element: tab('data'),
            route: base + '/data',
            popover: {
                title: '数据管理',
                description: '查看和管理检查数据。',
                side: 'bottom'
            }
        },
        {
            element: '.page-header',
            route: base + '/data',
            popover: {
                title: '数据操作栏',
                description: '• 选择问卷查看对应数据\n• 导出当前问卷或全部数据',
                side: 'bottom'
            }
        },
        {
            element: '.stats-row',
            route: base + '/data',
            popover: {
                title: '数据统计',
                description: '快速查看关键指标：\n• 总答卷数：收到的全部答卷\n• 今日/本周新增：实时监控数据回收\n• 平均用时：答卷填写时长参考',
                side: 'bottom'
            }
        },
        {
            element: '.filter-bar',
            route: base + '/data',
            popover: {
                title: '数据筛选栏',
                description: '精确筛选答卷数据：\n• 按波次筛选\n• 搜索店名/店代码\n• 按审核状态（待审核/通过/驳回）\n• 按申诉状态筛选\n• 「下发审核」批量下发数据',
                side: 'bottom'
            }
        },
        {
            element: '.data-table',
            route: base + '/data',
            popover: {
                title: '答卷列表',
                description: '查看和管理答卷数据：\n• 经销商代码和名称、所属波次\n• 提交时间、用时\n• 一审/二审/三审审核进度\n• 操作：查看详情、审核、删除',
                side: 'top'
            }
        },

        // ========= 申诉处理 =========
        {
            element: tab('appeal'),
            route: base + '/appeal',
            popover: {
                title: '申诉处理',
                description: '管理店端提交的申诉。',
                side: 'bottom'
            }
        },
        {
            element: '.filter-card',
            route: base + '/appeal',
            popover: {
                title: '申诉筛选栏',
                description: '筛选和查看申诉：\n• 选择申诉批次\n• 搜索经销商代码/名称\n• 查看申诉时间和状态',
                side: 'bottom'
            }
        },
        {
            element: '.appeal-table',
            route: base + '/appeal',
            popover: {
                title: '申诉记录列表',
                description: '展示申诉记录：\n• 经销商代码和名称\n• 申诉状态（待申诉/已提交/审核中/已完成）\n• 点击「查看详情」或「审核」处理',
                side: 'top'
            }
        },

        // ========= 单店报告（在报告下发之前） =========
        {
            element: tab('store-report'),
            route: base + '/store-report',
            popover: {
                title: '单店报告',
                description: '查看门店级检查成绩报告。',
                side: 'bottom'
            }
        },
        {
            element: '.store-report .page-header',
            route: base + '/store-report',
            popover: {
                title: '报告操作栏',
                description: '• 选择波次和问卷查看成绩\n• 搜索区域/门店名称\n• 生成/更新统计数据\n• 导出 Excel 报告\n• 发布报告到客户门户',
                side: 'bottom'
            }
        },
        {
            element: '.stats-bar',
            route: base + '/store-report',
            popover: {
                title: '统计概览',
                description: '关键统计指标：\n• 全国平均分：所有门店的平均得分\n• 已审核门店数：完成审核的门店总数',
                side: 'bottom'
            }
        },

        // ========= 报告下发 =========
        {
            element: tab('file-distribution'),
            route: base + '/file-distribution',
            popover: {
                title: '报告下发',
                description: '向店端下发检查报告。',
                side: 'bottom'
            }
        },
        {
            element: '.fd-actions',
            route: base + '/file-distribution',
            popover: {
                title: '报告操作栏',
                description: '• 点击「上传报告」上传检查报告文件\n• 按波次筛选已下发的报告\n• 搜索文件名或门店',
                side: 'bottom'
            }
        },
        {
            element: '.fd-card',
            route: base + '/file-distribution',
            popover: {
                title: '已下发文件列表',
                description: '管理已下发的报告文件：\n• 文件名、门店、类型、大小\n• 下载状态和下载次数\n• 支持在线预览 Excel 报告',
                side: 'top'
            }
        },

        // ========= 项目设置 =========
        {
            element: tab('settings'),
            route: base + '/settings',
            popover: {
                title: '项目设置',
                description: '配置项目参数和人员管理。',
                side: 'bottom'
            }
        },
        // 项目基本信息（第1个 settings-card）
        {
            element: '.settings-page > .settings-card:nth-child(1)',
            route: base + '/settings',
            popover: {
                title: '项目基本信息',
                description: '编辑项目核心信息：\n• 项目代号（不可修改）和名称\n• 项目备注说明\n• 项目状态（进行中/已完成）\n• 项目起止时间范围',
                side: 'bottom'
            }
        },
        // 团队成员管理（第2个 settings-card）
        {
            element: '.settings-page > .settings-card:nth-child(2)',
            route: base + '/settings',
            popover: {
                title: '团队成员管理',
                description: '管理项目参与人员：\n• 添加新成员并分配角色\n• 角色类型：研究/QC/执行/代理等\n• 编辑成员角色或移除成员\n• 查看成员状态和加入时间',
                side: 'top'
            }
        },
        // 客户管理（第4个 settings-card）
        {
            element: '.settings-page > .settings-card:nth-child(4)',
            route: base + '/settings',
            popover: {
                title: '客户管理',
                description: '管理项目关联的客户账号：\n• 导入客户名单（Excel）\n• 分配已有客户到项目\n• 设置客户数据权限级别\n  （全国/大区/小区/经销商）\n• 客户可在门户查看报告和成绩',
                side: 'top'
            }
        },
        // 代理管理（第5个 settings-card）
        {
            element: '.settings-page > .settings-card:nth-child(5)',
            route: base + '/settings',
            popover: {
                title: '代理管理',
                description: '管理项目关联的代理账号：\n• 导入代理名单（Excel）\n• 分配已有代理到项目\n• 代理负责执行检查和数据收集\n• 查看代理负责的区域和状态',
                side: 'top'
            }
        },

        // ========= 完成 =========
        {
            popover: {
                title: '引导完成',
                description: '您已了解项目的所有功能模块！\n\n建议操作顺序：\n1. 创建问卷（我的问卷）\n2. 创建波次并上传经销商（波次管理）\n3. 查看数据和审核（数据管理）'
            }
        }
    ]
}
